import { useNavigate } from "react-router";
import ReactEcharts from "echarts-for-react";
import { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import { miwChartColors, tooltipMark, utahChartColors } from "../../shared/reportUtil";
import { TutorialUsageByCategory } from "./UsageFilteredByCourseViewModel";

interface ChartData {
	categoryName: string;
	timeSpentMinutes: number;
	link: string;
}

function initializeOptions(data: ChartData[]): EChartsOption {
	const { config } = useAppSelector((state) => state.site);
	let chartColors = config?.isUtahComposeSite ? utahChartColors : miwChartColors;

	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	if (chartPalette) {
		chartColors = chartPalette.slice(0, 2);
	}

	const option: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		title: {
			text: "Lesson Usage by Category",
			top: 0,
			left: 0,
			padding: 0,
			textStyle: { fontSize: 16, fontWeight: 600, color: foregroundColor },
		},
		grid: {
			containLabel: true,
			top: 50,
			left: 30,
			bottom: 25,
		},
		yAxis: {
			type: "value",
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			name: "Total Time Spent (in minutes)",
			nameLocation: "middle",
			nameGap: 30,
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		xAxis: {
			type: "category",
			data: data.map((d) => ({
				value: d.categoryName,
				link: d.link,
			})),
			axisLabel: {
				margin: 20,
				width: 85,
				lineHeight: 15,
				interval: 0,
				overflow: "break",
				hideOverlap: false,
			},
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
		},
		tooltip: {
			trigger: "item",
			formatter: function (params: { value: string; name: string; color: string }) {
				return (
					params.name +
					"<br />" +
					tooltipMark(params.color) +
					"Time Spent: <b>" +
					params.value +
					"</b> min"
				);
			},
			textStyle: {
				fontSize: 12,
			},
		},
		dataGroupId: "",
		animationDurationUpdate: 500,
		series: {
			type: "bar",
			id: "category",
			data: data.map((d) => ({
				value: d.timeSpentMinutes,
				link: d.link,
			})),
			itemStyle: {
				color: function (param: { dataIndex: number }) {
					return chartColors[param.dataIndex % chartColors.length];
				},
			},
			label: {
				show: true,
				position: ["50%", "0%"],
				height: 25,
				width: 25,
				align: "center",
				verticalAlign: "middle",
				padding: 4,
				backgroundColor: "#fff",
				borderRadius: 25,
				borderColor: "inherit",
				borderWidth: 1,
				fontWeight: "bolder",
				fontSize: 12,
			},
			barMinWidth: 40,
			emphasis: {
				itemStyle: {
					shadowBlur: 10,
				},
			},
			cursor: "default",
		},
	};
	return option;
}

function lessonUsageLink(courseId: number, categoryId: number) {
	return `/reports/course/usage/lessonCategory?categoryId=${categoryId}&courseId=${courseId}`;
}

interface Props {
	className?: string;
	courseId: number;
	data: TutorialUsageByCategory[];
}

export function LessonCategoryUsageChart(props: Props) {
	const navigate = useNavigate();
	if (!props.data || props.data.length == 0) return "";

	const chartData = props.data.map((d) => ({
		categoryName: d.tutorialCategoryName,
		timeSpentMinutes: d.timeSpentMinutes,
		link: lessonUsageLink(props.courseId, d.tutorialCategoryId),
	}));
	const options = initializeOptions(chartData);

	const onChartClick = (params: any, echarts: any) => {
		if (params.data.link) navigate(params.data.link);
	};

	return (
		<ReactEcharts
			option={options}
			className={`w-full ${props.className}`}
			onEvents={{
				click: onChartClick,
			}}
		/>
	);
}
